import { useContext } from 'react'

import {
  Button, Columns, LoadingModule, SaveFormModule,
} from '@domain-group/suit-theme'

import useBrand, { useBrandForm } from '@hooks/useBrand'
import useCurrentUser from '@hooks/useCurrentUser'

import PageContext from '@contexts/pageContext'

import Contracts from '@components/Contracts'
import CustomLinks from '@components/CustomLinks'
import EntityActivity from '@components/EntityActivity'
import EntityAssets from '@components/EntityAssets'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import PageLayout from '../../../../components/PageLayout'

import BrandAddress from '../BrandAddress'
import BrandColours from '../BrandColours'
import BrandData from '../BrandData'
import BrandDetails from '../BrandDetails'
import BrandEditMediaReviewer from '../BrandEditMediaReviewer'
import BrandImages from '../BrandImages'
import BrandInactiveBanner from '../../../../components/BrandInactiveBanner'
import BrandSystemColours from '../BrandSystemColours'
import BrandThemes from '../BrandThemes'
import BrandTools from '../../../../components/BrandTools'

interface PageContentProps {
  brandId: number,
}

const PageContent = (props: PageContentProps) => {
  const { brandId } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks,
    callbacks:
    {
      deleteContract,
      updateBrand,
    },
  } = pageContext

  const brandPayload = useBrand({ id: brandId })
  const {
    brand,
    callbacks: {
      updateBrand: updateFn,
      createOrEditCustomLinks,
    },
    hasQuickLinks,
    loading,
    quickLinks,
    updating,
  } = brandPayload

  const { isAdmin, isBrandUser } = useCurrentUser()

  const brandFormPayload = useBrandForm(brand)
  const { entityState, saveEnabled } = brandFormPayload

  const brandUpdatePayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: entityState,
    toastText: 'Brand Updated',
  }

  return (
    <PageLayout entityPayload={brandPayload}>
      <Columns.Main>
        <LoadingModule loading={loading} times={3} />

        {!loading && (
          <>
            {brand.hidden && (
              <BrandInactiveBanner brandPayload={brandPayload} />
            )}

            <BrandDetails brandFormPayload={brandFormPayload} />

            <BrandAddress brandFormPayload={brandFormPayload} />

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              <BrandData brandFormPayload={brandFormPayload} />
            </ScopedConditionalContent>

            <EntityActivity subjectId={brandId} subjectType="Brand" />

            <SaveFormModule>
              <Button
                buttonStyle="primaryCreate"
                disabled={!saveEnabled}
                loading={updating}
                marginLeft="auto"
                onClick={() => updateBrand(brandUpdatePayload)}
                size="medium"
              >
                Save Details
              </Button>
            </SaveFormModule>
          </>
        )}
      </Columns.Main>

      <Columns.Sidebar>
        {(isAdmin || isBrandUser) && (
          <BrandTools brandPayload={brandPayload} />
        )}

        <BrandColours brand={brand} />

        <BrandImages brandPayload={brandPayload} />

        <BrandEditMediaReviewer brandPayload={brandPayload} />

        <BrandThemes brand={brand} />

        <CustomLinks
          callbacks={{
            createOrEditCustomLinks: () => createOrEditCustomLinks({
              callbacks: {
                updateEntity: updateFn,
              },
            }),
          }}
          hasQuickLinks={hasQuickLinks}
          loading={loading!}
          quickLinks={quickLinks}
        />

        <EntityAssets
          callbacks={callbacks}
          filters={{ attachableId: brandId, attachableType: 'Brand', attachmentType: 'brand_asset' }}
        />

        <BrandSystemColours brandFormPayload={brandFormPayload} />

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <Contracts callbacks={{ deleteContract }} ownerId={brandId} ownerType="Brand" />
        </ScopedConditionalContent>
      </Columns.Sidebar>
    </PageLayout>
  )
}

export default PageContent
